// src/pages/Chat.jsx

import React, { useEffect, useRef, useState, useMemo } from "react";
import { socket, connectSocketOnce } from "../socket";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import "../theme/chat.css";
import api from "../api";
import { createPortal } from "react-dom";





export default function Chat() {
  const { conversationId } = useParams(); 
  useEffect(() => {
  connectSocketOnce();
}, []);
  
  const isNewChat = conversationId === "new";
  
  
  const location = useLocation();
  const otherUser = location.state?.user || null;

  const me = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  }, []);
  
  const [chatUser, setChatUser] = useState(otherUser);

  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const messagesRef = useRef(null);
  

  useEffect(() => {
  if (otherUser) {
    setChatUser(otherUser);
  }
}, [otherUser]);

const [typing, setTyping] = useState(false);
const [onlineUsers, setOnlineUsers] = useState(() => new Set());
const typingTimeoutRef = useRef(null);
const typingTimerRef = useRef(null);
const reconnectTimeoutRef = useRef(null);
  
  const firstLoadRef = useRef(true);
  const navigate = useNavigate();
  const [isSocketConnected, setIsSocketConnected] =
  useState(socket.connected);
  
  
  
  useEffect(() => {
  if (isNewChat && !otherUser) {
  navigate("/conversations");
}
}, [isNewChat, otherUser, navigate]);

  /* ---------------- SOCKET ---------------- */
  useEffect(() => {
  if (!conversationId || isNewChat) return;

  setReady(false);
  
  const onConnect = () => setIsSocketConnected(true);
const onDisconnect = () => setIsSocketConnected(false);

socket.on("connect", onConnect);
socket.on("disconnect", onDisconnect);

  const join = () => {
  if (!conversationId || conversationId === "new") return;

  socket.emit("join_conversation", conversationId);
  socket.emit("get_online_users");
};

  const onJoinedConversation = (joinedId) => {
    if (String(joinedId) !== String(conversationId)) return;

    setReady(true);
    socket.emit("mark_read", { conversationId });
    socket.emit("chat:active", { conversationId });
  };

  const onMessage = (data) => {
    if (String(data.conversation_id) !== String(conversationId)) return;
    setMessages((prev) =>
      prev.some((m) => m.id === data.message.id)
        ? prev
        : [...prev, data.message]
    );
  };

  const safeJoin = () => {
  join();

  clearTimeout(reconnectTimeoutRef.current);

  reconnectTimeoutRef.current = setTimeout(() => {
  console.log("Retrying conversation join...");
  join();
}, 3000);
};

if (socket.connected) {
  safeJoin();
} else {
  socket.once("connect", safeJoin);
}

  socket.on("joined_conversation", onJoinedConversation);
  socket.on("message", onMessage);
  

  const onNotification = (n) => {
    if (n.type !== "message") return;
    if (String(n.payload?.conversation_id) === String(conversationId)) {
      return;
    }
  };

  socket.on("notification", onNotification);

  const onTyping = ({ userId }) => {
    if (String(userId) === String(me?.id)) return;

    setTyping(true);

    clearTimeout(typingTimerRef.current);

    typingTimerRef.current = setTimeout(() => {
  setTyping(false);
}, 1500);
};

  const onStopTyping = ({ userId }) => {
    if (String(userId) === String(me?.id)) return;
    setTyping(false);
  };

  socket.on("typing", onTyping);
  socket.on("stop_typing", onStopTyping);

  const onUserOnline = ({ userId }) => {
    setOnlineUsers((prev) => {
      const s = new Set(prev);
      s.add(String(userId));
      return s;
    });
  };

  const onUserOffline = ({ userId }) => {
    setOnlineUsers((prev) => {
      const s = new Set(prev);
      s.delete(String(userId));
      return s;
    });
  };

  socket.on("user_online", onUserOnline);
  socket.on("user_offline", onUserOffline);

  const onOnlineUsers = (list) => {
    setOnlineUsers(new Set(list.map(String)));
  };

  socket.on("online_users", onOnlineUsers);

  const onMessagesRead = ({ conversationId: cid, readerId }) => {
    if (String(readerId) === String(me?.id)) return;
    if (String(cid) !== String(conversationId)) return;

    setMessages((prev) =>
      prev.map((m) =>
        m.sender_id === me?.id
          ? { ...m, is_read: true }
          : m
      )
    );
  };

  socket.on("messages_read", onMessagesRead);


  return () => {
    socket.emit("chat:inactive", { conversationId });

    socket.off("joined_conversation", onJoinedConversation);
    socket.off("message", onMessage);
    socket.off("notification", onNotification);
    socket.off("typing", onTyping);
    socket.off("stop_typing", onStopTyping);
    socket.off("user_online", onUserOnline);
    socket.off("user_offline", onUserOffline);
    socket.off("online_users", onOnlineUsers);
    socket.off("messages_read", onMessagesRead);
    
    socket.off("connect", onConnect);
socket.off("disconnect", onDisconnect);
    
    clearTimeout(typingTimeoutRef.current);
    clearTimeout(typingTimerRef.current);
    
    clearTimeout(reconnectTimeoutRef.current);
  };
}, [conversationId, isNewChat, me?.id]);


  /* ---------------- LOAD MESSAGES ---------------- */
  useEffect(() => {
  if (!conversationId || isNewChat) {
    setLoading(false);
    setReady(true);
    return;
  }

  (async () => {
    try {
      const { data } = await api.get(`/conversations/${conversationId}/messages`);
      const loadedMessages = data.messages || [];

      firstLoadRef.current = true;
      setMessages(loadedMessages);

      if (!otherUser && loadedMessages.length) {
        const sample = loadedMessages[0];
        const other =
          String(sample.sender_id) === String(me?.id)
            ? {
                id: sample.receiver_id,
                username: sample.receiver_username || "User",
                user_id: sample.receiver_user_id || "",
                profile_pic_url: sample.receiver_profile_pic_url || ""
              }
            : {
                id: sample.sender_id,
                username: sample.username || "User",
                user_id: sample.user_id || "",
                profile_pic_url: sample.profile_pic_url || ""
              };

        setChatUser(other);
      } else if (otherUser) {
        setChatUser(otherUser);
      }

      setReady(true);
    } catch (err) {
      console.error("Load messages failed", err);
    } finally {
      setLoading(false);
    }
  })();
}, [conversationId, isNewChat, otherUser, me?.id]);

  /* ---------------- AUTO SCROLL ---------------- */
  useEffect(() => {
  const el = messagesRef.current;
  if (!el) return;

  setTimeout(() => {
    el.scrollTop = el.scrollHeight;
  }, 0);
}, [messages]);



  /* ---------------- SEND ---------------- */
  const sendMessage = async () => {
  const messageText = text.trim();
  const targetUser = otherUser || chatUser;

  if (!messageText || !targetUser?.id) return;

  if (!isNewChat && conversationId) {
    socket.emit("stop_typing", { conversationId });
  }

  try {
    const { data } = await api.post(
      `/conversations/with/${targetUser.id}`,
      { text: messageText }
    );

    setMessages((prev) =>
      prev.some((m) => m.id === data.message.id)
        ? prev
        : [...prev, data.message]
    );
    
    socket.emit("message_sent_local", {
      conversation_id: data.conversation_id,
      text: messageText,
      created_at: data.message.created_at,
    });

    if (isNewChat && data.conversation_id) {
      navigate(`/chat/${data.conversation_id}`, {
        replace: true,
        state: { user: targetUser }
      });
    }
    
    
    setText("");
  } catch (e) {
    console.error("Send failed", e);
  }
};


const isOnline = onlineUsers.has(String(chatUser?.id));

const header = (
  <div
    style={{
      position: "fixed",
      top: "env(safe-area-inset-top)",
      left: 0,
      right: 0,
      height: 56,
      display: "flex",
      alignItems: "center",
      padding: "0 12px",
      borderBottom: "1px solid #1f2937",
      background: "#020617",
      zIndex: 9999,
    }}
  >
    <button
      onClick={() => navigate(-1)}
      style={{
        marginRight: 10,
        background: "none",
        border: "none",
        color: "#fff",
        fontSize: 18,
        cursor: "pointer",
      }}
    >
      ←
    </button>

    <div style={{ display: "flex", alignItems: "center" }}>
      
      {/* Dot */}
      <div
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: isOnline ? "#22c55e" : "#6b7280",
          boxShadow: isOnline ? "0 0 6px #22c55e" : "none",
        }}
      />

      {/* Text */}
      <div style={{ display: "flex", flexDirection: "column", marginLeft: 6 }}>
        <div style={{ fontWeight: 600 }}>
          {chatUser?.username || "User"}
        </div>

        {!isSocketConnected && (
          <div style={{ fontSize: 12, color: "#9ca3af" }}>
            Connecting...
          </div>
        )}

        {typing && (
          <div style={{ fontSize: 12, color: "#9ca3af" }}>
            Typing...
          </div>
        )}
      </div>
    </div>

  </div>
);

const overlayRoot = document.getElementById("overlay-root");


return (
  <>
    {overlayRoot && createPortal(header, overlayRoot)}
    {/* 🔥 CHAT BODY */}
    <div
  className="chat-page"
  style={{
    paddingTop: 56,
  }}
>

      {/* MESSAGES */}
      <div
  ref={messagesRef}
  className="chat-messages"
>
        {messages.map((m) => {
          const mine = me && m.sender_id === me.id;

          return (
            <div
              key={m.id}
              style={{
                display: "flex",
                justifyContent: mine ? "flex-end" : "flex-start",
                marginBottom: 12,
              }}
            >
              <div
  style={{
    background: mine ? "#2563eb" : "#1e293b",
color: "#fff",
    padding: "10px 14px",
    borderRadius: 14,
    maxWidth: "75%",
    display: "flex",
    alignItems: "center",
    gap: 6,
  }}
>
  <span>{m.text}</span>

  {mine && (
    m.is_read ? (
      <span style={{ fontSize: 12, color: "#38bdf8" }}>✓✓</span>
    ) : (
      <span style={{ fontSize: 12, opacity: 0.6 }}>✓</span>
    )
  )}
</div>
            </div>
          );
        })}
      </div>

      {/* INPUT BAR */}
      <div className="chat-input-bar">
        <input
  className="chat-input"
  value={text}
  placeholder={isSocketConnected ? "Type a message..." : "Connecting..."}
  disabled={!isSocketConnected}
  onChange={(e) => {
    setText(e.target.value);

    if (!conversationId || isNewChat) return;

    socket.emit("typing", { conversationId });

    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit("stop_typing", { conversationId });
    }, 1000);
  }}
  onKeyDown={(e) => e.key === "Enter" && sendMessage()}
/>

        <button className="chat-send-btn" onClick={sendMessage}>
          Send
        </button>
      </div>
    </div>
  </>
);
}